import { Card } from '@consta/uikit/Card';
import { Text } from '@consta/uikit/Text';
import { Table } from '@consta/uikit/Table';
import { Pagination } from '@consta/uikit/Pagination'; // Добавляем импорт
import { TextField } from '@consta/uikit/TextField';
import { Button } from '@consta/uikit/Button';
import { NavLink } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import classes from './style/ProjectInfo.module.scss';
import { useLazyGetProjectUuidQuery, useLazyGetProjectCommentsQuery } from '../../../../../services/projectService';
import { useLazyGetWellByProjectQuery } from '../../../../../services/wellService';
import { usePostInterpolationMapMutation } from '../../../../../services/commentService';
import { Well } from '../../../../../services/types/TWellServices';
import { Project } from '../../../../../services/types/TProjectServices';
import { ProjectGetCommentsResponse } from '../../../../../services/types/TProjectServices';

interface ProjectInfoProps {
  projectUuid?: string;
}

interface UserInfo {
  id: number;
  uuid: string;
  first_name: string;
  last_name: string;
  middle_name: string | null;
  username: string;
  email: string;
  company: {
    uuid: string;
    name: string;
  };
  role: {
    name: string;
    access_level: number;
  };
}

const ProjectInfo = ({ projectUuid }: ProjectInfoProps) => {
  const [projectData, setProjectData] = useState<Project | null>(null);
  const [wells, setWells] = useState<Well[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Lazy запрос данных проекта
  const [getProject, { data: projectResponse, isLoading: projectLoading, isError: projectError }] = useLazyGetProjectUuidQuery();

  // Lazy запрос скважин проекта
  const [getWells, { data: wellsData, isLoading: wellsLoading, isError: wellsError }] = useLazyGetWellByProjectQuery();

  useEffect(() => {
    if (projectUuid) {
      getProject({ uuid: projectUuid });
      getWells({ project_uuid: projectUuid });
      setCurrentPage(1); // Сбрасываем страницу при изменении проекта
    }
  }, [projectUuid, getProject, getWells]);

  useEffect(() => {
    if (projectResponse) {
      setProjectData(projectResponse);
    }
  }, [projectResponse]);

  useEffect(() => {
    if (wellsData) {
      // Обрабатываем данные из ответа API
      if (wellsData.results && Array.isArray(wellsData.results)) {
        setWells(wellsData.results);
      } else if (Array.isArray(wellsData)) {
        setWells(wellsData);
      } else {
        setWells([]);
      }
    }
  }, [wellsData]);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Не указана';
    try {
      return new Date(dateString).toLocaleDateString('ru-RU');
    } catch {
      return 'Неверная дата';
    }
  };

  // Функция для получения данных текущей страницы
  const getPaginatedData = (data: any[], page: number) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  // Добавляем индекс к данным
  const addIndexToData = (data: any[], page: number) => {
    return data.map((item, idx) => ({
      ...item,
      rowIndex: (page - 1) * itemsPerPage + idx + 1
    }));
  };

  // Преобразование данных скважин для таблицы согласно структуре ответа API
  const allRows = wells.map((well: Well) => ({
    name: well.number?.toString() || 'Не указан',
    workshop: well.workshop?.toString() || 'Не указан',
    brigade: well.team?.toString() || 'Не указан',
    cipher: well.horizons_info?.map((horizon: any) => horizon.name).join(', ') || 'Не указан',
    code: well.horizons_info?.map((horizon: any) => horizon.code).join(', ') || 'Не указан',
    plast: well.layers_info?.map((layer: any) => layer.name).join(', ') || 'Не указан',
    liquid: well.liquid_rate?.toString() || '0',
    water: well.water_cut?.toString() || '0',
    category: well.category_name || 'Не указана'
  }));

  const totalWells = allRows.length;
  const totalPages = Math.ceil(totalWells / itemsPerPage);
  const paginatedRows = addIndexToData(getPaginatedData(allRows, currentPage), currentPage);

  const columns = [
    { title: '№ добывающей скважины', accessor: 'name' },
    { title: 'Цех', accessor: 'workshop' },
    { title: 'Бригада', accessor: 'brigade' },
    { title: 'Название горизонта', accessor: 'cipher' },
    // { title: 'Шифр горизонта', accessor: 'code' },
    { title: 'Название пласта', accessor: 'plast' },
    { title: 'Дебит скважины по жидкости', accessor: 'liquid' },
    { title: 'Обводнённость (%)', accessor: 'water' },
    { title: 'Категория', accessor: 'category' },
  ];

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const isLoading = projectLoading || wellsLoading;
  const hasError: any = projectError || wellsError;

  return (
    <div className={classes.main}>
      <div className={classes.header}>
        <Text size='2xl' weight='bold'>Данные о проекте {projectData?.number ? `№ ${projectData.number}` : projectUuid}</Text>
        <Text size='s' view='secondary'>Подробная информация и связанные скважины</Text>
      </div>

      <div className={classes.container}>
        {isLoading && (
          <Text>Загрузка данных проекта...</Text>
        )}

        {hasError && (
          <Text view="alert">Ошибка при загрузке данных проекта</Text>
        )}

        {!isLoading && !hasError && projectData && (
          <div className={classes.projectInfoCard}>
            <Text className={classes.leftText} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <Text size='m' weight='bold'>Описание:</Text>
              <Text size='s' view='secondary' style={{ lineHeight: '1.5' }}>{projectData?.description || 'Не указано'}</Text>
            </Text>
            <Card style={{ padding: '20px', backgroundColor: 'rgb(249 250 251)' }}>
              <Text style={{ paddingBottom: '20px' }} size='l' weight='bold'>Информация о проекте:</Text>
              <div className={classes.text}>
                <div className={classes.left}>
                  <Text className={classes.leftText}>
                    <Text size='s' weight='bold'>Номер проекта:</Text>
                    <Text size='s' view='secondary'>{projectData?.number || 'Не указан'}</Text>
                  </Text>
                  <Text className={classes.leftText}>
                    <Text size='s' weight='bold'>Дата начала:</Text>
                    <Text size='s' view='secondary'>{projectData?.start_date ? formatDate(projectData.start_date) : 'Не указана'}</Text>
                  </Text>
                  <Text className={classes.leftText}>
                    <Text size='s' weight='bold'>Месторождения:</Text>
                    <Text size='s' view='secondary'>{projectData?.fields_info?.map((item: any) => item.name)?.join(', ') || 'Не указаны'}</Text>
                  </Text>
                </div>
                <div className={classes.right}>
                  <Text className={classes.rightText}>
                    <Text size='s' weight='bold'>Наименование:</Text>
                    <Text size='s' view='secondary'>{projectData?.name || 'Не указано'}</Text>
                  </Text>
                  <Text className={classes.rightText}>
                    <Text size='s' weight='bold'>Заказчик:</Text>
                    <Text size='s' view='secondary'>{projectData?.company_name || 'Не указан'}</Text>
                  </Text>
                  <Text className={classes.rightText}>
                    <Text size='s' weight='bold'>Дата завершения:</Text>
                    <Text size='s' view='secondary'>{projectData?.end_date ? formatDate(projectData.end_date) : 'Не указана'}</Text>
                  </Text>
                  <Text className={classes.rightText}>
                    <Text size='s' weight='bold'>Текущий этап:</Text>
                    <Text size='s' view='secondary'>{projectData?.current_stage || 'Не указан'}</Text>
                  </Text>
                </div>
              </div>
            </Card>
          </div>
        )}

        <div className={classes.table}>
          <Text weight='bold' size='xl'>Связанные скважины</Text>
          <Text size='s' view='secondary' style={{ marginBottom: '16px' }}>
            Показано: {paginatedRows.length} из {totalWells} скважин
          </Text>
          
          {wellsLoading && (
            <Text>Загрузка данных о скважинах...</Text>
          )}
          
          {wellsError && (
            <Text view="alert">Ошибка при загрузке данных о скважинах</Text>
          )}
          
          {!wellsLoading && !wellsError && wells.length > 0 && (
            <>
              <Table 
                rows={paginatedRows} 
                columns={columns} 
                borderBetweenRows 
                stickyHeader
              />
              
              {totalPages > 1 && (
                <div className={classes.paginationSection} style={{ 
                  marginTop: '20px', 
                  display: 'flex', 
                  justifyContent: 'center' 
                }}>
                  <Pagination
                    items={totalPages}
                    value={currentPage}
                    onChange={handlePageChange}
                    showFirstPage
                    showLastPage
                    visibleCount={7}
                    size='s'
                  />
                </div>
              )}
            </>
          )}
          
          {!wellsLoading && !wellsError && wells.length === 0 && (
            <Text>Нет данных о скважинах для этого проекта</Text>
          )}
        </div>

        <CommentsSection projectUuid={projectUuid} />
      </div>
    </div>
  );
};

const CommentsSection = ({ projectUuid }: { projectUuid?: string }) => {
  const [comments, setComments] = useState<ProjectGetCommentsResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState<any>('');

  // Lazy запрос комментариев проекта
  const [getComments] = useLazyGetProjectCommentsQuery();

  // Мутация для создания комментария
  const [createComment, { isLoading: isCreating }] = usePostInterpolationMapMutation();

  // Функция для получения информации о пользователе из localStorage
  const getUserInfo = (): UserInfo | null => {
    try {
      const userInfo = localStorage.getItem('userInfo');
      if (userInfo) {
        return JSON.parse(userInfo);
      }
    } catch (error) {
      console.error('Error parsing userInfo from localStorage:', error);
    }
    return null;
  };

  // Функция для получения полного имени пользователя
  const getUserFullName = (): string => {
    const userInfo = getUserInfo();
    if (userInfo) {
      const { first_name, last_name, middle_name } = userInfo;
      return `${last_name} ${first_name}${middle_name ? ` ${middle_name}` : ''}`.trim();
    }
    return 'Анонимный пользователь';
  };

  // Функция для получения UUID пользователя
  const getUserUuid = (): string | null => {
    const userInfo = getUserInfo();
    return userInfo?.uuid || null;
  };

  // Функция для обновления списка комментариев
  const updateComments = async () => {
    if (!projectUuid) return;

    try {
      const response = await getComments({ project_uuid: projectUuid }).unwrap();
      // Преобразуем ответ в массив комментариев
      if (Array.isArray(response)) {
        setComments(response);
      } else {
        // Если ответ - одиночный комментарий, оборачиваем в массив
        setComments([response]);
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
      setError('Ошибка при загрузке комментариев');
    }
  };

  useEffect(() => {
    const fetchComments = async () => {
      if (!projectUuid) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await getComments({ project_uuid: projectUuid }).unwrap();
        // Преобразуем ответ в массив комментариев
        if (Array.isArray(response)) {
          setComments(response);
        } else {
          // Если ответ - одиночный комментарий, оборачиваем в массив
          setComments([response]);
        }
      } catch (err) {
        setError('Ошибка при загрузке комментариев');
        console.error('Error fetching comments:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchComments();
  }, [projectUuid]);

  // ФУНКЦИЯ ОТПРАВКИ КОММЕНТАРИЯ
  const handleAddComment = () => {
    const commentText = newComment?.trim();
    if (!commentText || !projectUuid) return;

    const userUuid = getUserUuid();
    if (!userUuid) {
      setError('Не удалось определить пользователя');
      return;
    }

    const commentData = {
      body: commentText,
      project: projectUuid,
      author: userUuid
    };

    // Отправляем комментарий и обновляем список
    createComment(commentData).unwrap()
      .then(() => {
        // Очищаем поле ввода
        setNewComment('');
        // ОБНОВЛЯЕМ список комментариев
        updateComments();
      })
      .catch((err) => {
        console.error('Error creating comment:', err);
        setError('Ошибка при создании комментария');
      });
  };

  // ФУНКЦИЯ ОТПРАВКИ ОТВЕТА
  const handleReply = (parentCommentUuid: string) => {
    const replyTextValue = replyText?.trim();
    if (!replyTextValue || !projectUuid) return;

    const userUuid = getUserUuid();
    if (!userUuid) {
      setError('Не удалось определить пользователя');
      return;
    }

    const replyData = {
      body: replyTextValue,
      project: projectUuid,
      author: userUuid,
      parent: parentCommentUuid
    };

    // Отправляем ответ и обновляем список
    createComment(replyData).unwrap()
      .then(() => {
        // Очищаем поле ответа и закрываем форму
        setReplyText('');
        setReplyingTo(null);
        // ОБНОВЛЯЕМ список комментариев
        updateComments();
      })
      .catch((err) => {
        console.error('Error creating reply:', err);
        setError('Ошибка при создании ответа');
      });
  };

  const handleStartReply = (commentUuid: string) => {
    setReplyingTo(commentUuid);
    setReplyText('');
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setReplyText('');
  };

  const formatCommentDate = (dateString: string) => {
    if (!dateString) return 'Дата не указана';
    try {
      return new Date(dateString).toLocaleDateString('ru-RU');
    } catch {
      return 'Неверная дата';
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddComment();
    }
  };

  const handleReplyKeyPress = (e: React.KeyboardEvent, parentCommentUuid: string) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleReply(parentCommentUuid);
    }
  };

  // УПРОЩЕННАЯ ПРОВЕРКА - можно ли отвечать на комментарий
  const canReplyToComment = (comment: ProjectGetCommentsResponse) => {
    // Если есть child с данными - нельзя отвечать
    if (comment.child && comment.child.uuid && comment.child.body) {
      return false;
    }
    // Во всех остальных случаях можно отвечать
    return true;
  };

  // Проверка возможности отправки
  const canSubmitComment = Boolean(
    projectUuid &&
    newComment?.trim() &&
    getUserUuid()
  );

  const canSubmitReply = Boolean(
    projectUuid &&
    replyText?.trim() &&
    getUserUuid()
  );

  return (
    <div className={classes.comment}>
      <Text weight='bold' size='xl'>Комментарии и вопросы</Text>

      {isLoading && (
        <Text>Загрузка комментариев...</Text>
      )}

      {error && (
        <Text view="alert">{error}</Text>
      )}

      <div className={classes.commentCards}>
        {!isLoading && !error && comments.length === 0 && (
          <Text view="secondary">Комментариев пока нет</Text>
        )}

        {comments.map((comment) => (
          <div key={comment.uuid}>
            <CommentCard
              author={comment.user_fio || getUserFullName()}
              date={formatCommentDate(comment.created_at)}
              text={comment.body}
              onReply={() => handleStartReply(comment.uuid)}
              canReply={canReplyToComment(comment)}
            />

            {/* Форма ответа на комментарий */}
            {replyingTo === comment.uuid && (
              <Card verticalSpace="m" horizontalSpace="m" style={{ marginTop: '10px', marginLeft: '20px' }}>
                <TextField
                  type="textarea"
                  placeholder="Введите ваш ответ..."
                  value={replyText}
                  onChange={setReplyText}
                  onKeyPress={(e) => handleReplyKeyPress(e, comment.uuid)}
                  rows={3}
                />
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px', justifyContent: 'flex-end' }}>
                  <Button
                    size="xs"
                    label="Отмена"
                    onClick={handleCancelReply}
                  />
                  <Button
                    size="xs"
                    label="Ответить"
                    onClick={() => handleReply(comment.uuid)}
                    disabled={!canSubmitReply}
                    loading={isCreating}
                  />
                </div>
              </Card>
            )}

            {/* Отображение дочерних комментариев (ответов) */}
            {comment.child && comment.child.uuid && comment.child.body && (
              <div style={{ marginLeft: '20px', marginTop: '10px' }}>
                <CommentCard
                  author={comment.child.user_fio || getUserFullName()}
                  date={formatCommentDate(comment.child.created_at)}
                  text={comment.child.body}
                  onReply={() => { }}
                  canReply={false}
                  isChild={true}
                />
              </div>
            )}
          </div>
        ))}

        <AddCommentCard
          value={newComment}
          onChange={setNewComment}
          onAdd={handleAddComment}
          onKeyPress={handleKeyPress}
          isLoading={isCreating}
          disabled={!canSubmitComment}
        />
      </div>
    </div>
  );
};

interface CommentCardProps {
  author: string;
  date: string;
  text: string;
  onReply: () => void;
  canReply: boolean;
  isChild?: boolean;
}

const CommentCard = ({ author, date, text, onReply, canReply, isChild = false }: CommentCardProps) => (
  <Card
    verticalSpace="l"
    horizontalSpace="l"
    className={classes.cardContainerOptions}
    style={isChild ? { backgroundColor: 'rgba(0, 0, 0, 0.02)', borderLeft: '3px solid #0078D2' } : {}}
  >
    <Text size='s' style={{ display: 'flex', flexDirection: 'row' }}>
      {author} - <Text size='s' view='secondary'> {date}</Text>
    </Text>
    <Text size='s'>{text}</Text>
    {canReply && (
      <div style={{ display: 'flex', justifyContent: 'end' }}>
        <Button
          label='Ответить на комментарий'
          size='xs'
          onClick={onReply}
        />
      </div>
    )}
  </Card>
);

interface AddCommentCardProps {
  value: string;
  onChange: (value: any) => void;
  onAdd: () => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  isLoading: boolean;
  disabled: boolean;
}

const AddCommentCard = ({ value, onChange, onAdd, onKeyPress, isLoading, disabled }: AddCommentCardProps) => (
  <Card className={classes.addComment}>
    <TextField
      type="textarea"
      label='Оставить комментарий'
      placeholder='Введите ваш комментарий'
      value={value}
      onChange={onChange}
      onKeyPress={onKeyPress}
      disabled={isLoading}
      rows={3}
    />
    <Button
      style={{ width: '200px' }}
      label={isLoading ? 'Отправка...' : 'Отправить'}
      onClick={onAdd}
      disabled={disabled}
      loading={isLoading}
    />
  </Card>
);

export default ProjectInfo;